import React from 'react';
import PropTypes from 'prop-types';
import { formatTime, isoDateToString } from '../../utils/formatUnits';

export function SignMetadata(props) {
  const generateName = user => {
    if (!user) {
      return 'N/A';
    }

    const { firstname, lastname, name } = user;

    if (firstname.trim() === '' || lastname.trim() === '') {
      return name;
    }

    return `${firstname} ${lastname}`;
  };

  const getUserFromUsers = (userId, users) => {
    if (!userId) {
      return false;
    }

    const userPosition = users.findIndex(user => user.user_id === userId);

    if (userPosition === -1) {
      return false;
    }

    return users[userPosition];
  };

  const generateChars = user => {
    const chars = [];

    if (user.firstname?.trim().length === 0 && user.lastname?.trim().length === 0) {
      chars[0] = user.name[0].toUpperCase();
    } else {
      if (user.firstname && user.firstname.length !== 0) {
        chars.push(user.firstname[0].toUpperCase());
      }

      if (user.lastname && user.lastname.length !== 0) {
        chars.push(user.lastname[0].toUpperCase());
      }
    }

    return chars;
  };

  const getEventInfo = (userId, users, date) => {
    const user = getUserFromUsers(userId, users);
    const chars = generateChars(user);
    const name = generateName(user);

    return {
      dateString: isoDateToString(date),
      avatarLetters: chars.join(''),
      name,
    };
  };

  const { sign, users = [] } = props;

  if (!sign?.createdDate || !users?.length) {
    return null;
  }

  const getDuration = time => {
    if (!time || time < 0) {
      return null;
    }

    const [secs, ms] = formatTime(time / 1000).split('.');

    return ms ? `${secs}.${ms}` : secs;
  };

  const getBool = data => (data ? 'Yes' : 'No');
  const {
    modifiedBy = '',
    modifiedDate = '',
    publishedDate = '',
    createdBy,
    createdDate,
    width = '',
    height = '',
    duration = '',
    template,
    id,
  } = sign;
  const {
    dateString: createdDateString,
    avatarLetters: creatorAvatarLetters,
    name: creatorName,
  } = getEventInfo(createdBy, users, createdDate);
  const {
    dateString: modifiedDateString,
    avatarLetters: modifiedAvatarLetter,
    name: modifierName,
  } = getEventInfo(modifiedBy, users, modifiedDate);
  const durationString = getDuration(duration);
  const templateText = getBool(template);
  const publishedDateString = publishedDate ? isoDateToString(publishedDate) : null;

  return (
    <div data-test="metadata-container" className="flex items-center justify-center font-sans">
      <div className="w-full bg-white rounded-[2.5rem] shadow-[0_20px_50px_rgba(0,0,0,0.05)] p-4 border border-gray-100">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
          <div className="space-y-8">
            <div>
              <h3 className="text-[10px] uppercase tracking-[0.2em] text-gray-400 font-bold mb-4">
                Timeline
              </h3>
              <div className="relative pl-6 border-l border-gray-100 space-y-8">
                <div className="relative">
                  <div className="absolute -left-[29px] top-1 w-3 h-3 rounded-full bg-black border-4 border-white ring-1 ring-black" />
                  <p className="!mb-0 text-sm font-bold text-black leading-none">Modified Date</p>
                  <p
                    className="!mb-0 text-xs text-gray-400 mt-1"
                    data-test="metadata-modified-date"
                  >
                    {modifiedDateString}
                  </p>
                </div>
                <div className="relative">
                  <div className="absolute -left-[29px] top-1 w-3 h-3 rounded-full bg-gray-200 border-4 border-white" />
                  <p className="!mb-0 text-sm font-bold text-gray-400 leading-none">Created Date</p>
                  <p className="!mb-0 text-xs text-gray-300 mt-1" data-test="metadata-created-date">
                    {createdDateString}
                  </p>
                </div>
                {publishedDateString && (
                  <div className="relative">
                    <div className="absolute -left-[29px] top-1 w-3 h-3 rounded-full bg-gray-200 border-4 border-white" />
                    <p className="!mb-0 text-sm font-bold text-gray-400 leading-none">
                      Published Date
                    </p>
                    <p
                      className="!mb-0 text-xs text-gray-300 mt-1"
                      data-test="sign-metadata-published-date"
                    >
                      {publishedDateString}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="space-y-8">
            <div>
              <h3 className="text-[10px] uppercase tracking-[0.2em] text-gray-400 font-bold mb-4 ">
                Ownership
              </h3>
              <div className="space-y-6">
                <div className="flex items-center group ">
                  <div className="w-10 h-10 rounded-xl bg-black flex items-center justify-center text-white text-xs font-bold transition-transform group-hover:scale-110">
                    {creatorAvatarLetters}
                  </div>
                  <div className="ml-4">
                    <p className="!mb-0 text-[10px] text-gray-400 font-bold uppercase tracking-tight">
                      Creator
                    </p>
                    <p className="!mb-0 text-sm font-bold text-black" data-test="metadata-creator">
                      {creatorName}
                    </p>
                  </div>
                </div>

                <div className="flex items-center group ">
                  <div className="w-10 h-10 rounded-xl border border-gray-200 flex items-center justify-center text-black text-xs font-bold transition-transform group-hover:scale-110">
                    {modifiedAvatarLetter}
                  </div>
                  <div className="ml-4">
                    <p className="!mb-0 text-[10px] text-gray-400 font-bold uppercase tracking-tight">
                      Last Modified
                    </p>
                    <p className="!mb-0 text-sm font-bold text-black" data-test="metadata-modifier">
                      {modifierName}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-8 w-[155px] h-[195px]">
            <div>
              <h3 className="text-[10px] uppercase tracking-[0.2em] text-gray-400 font-bold mb-4 ">
                Thumbnail
              </h3>
              <div className="rounded-[2rem] border border-gray-100 bg-white p-2 shadow-[0_15px_40px_rgba(0,0,0,0.04)]">
                <div className="group relative flex  items-center justify-center overflow-hidden rounded-[1.5rem] border border-gray-50 bg-gray-100">
                  <img
                    src={`/cms/data/v1/signs/${id}/pending/thumbnail?${sign.modifiedDate || ''}`}
                    alt="sign1"
                    className="w-full h-full object-cover max-h-[140px]"
                    onError={e => {
                      e.currentTarget.src = '/assets/images/icons/thumbnail.jpeg';
                    }}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="bg-gray-50 rounded-[2rem] p-6 space-y-6">
            <h3 className="text-[8px] xl:text-[10px] uppercase tracking-[0.2em] text-gray-400 font-bold">
              Specifications
            </h3>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="!mb-0 text-[8px] xl:text-[10px] font-bold text-gray-400 uppercase">
                  Dimensions
                </p>
                <p
                  className="!mb-0 text-[8px] xl:text-sm font-black text-black"
                  data-test="metadata-dimensions"
                >
                  {width}
                  <span className="text-gray-300 font-light mx-1  text-[8px] xl:text-sm ">×</span>
                  {height}
                </p>
              </div>
              <div>
                <p className="!mb-0 text-[8px] xl:text-[10px] font-bold text-gray-400 uppercase">
                  Duration
                </p>
                <p
                  className="!mb-0 text-[8px] xl:text-sm font-black text-black"
                  data-test="metadata-duration"
                >
                  {durationString}
                  <span className="ml-0.5 text-gray-400 font-medium text-[8px] xl:text-[10px] ">
                    s
                  </span>
                </p>
              </div>
            </div>

            <div className="pt-4 border-t border-gray-200 flex justify-between items-center">
              <p className="!mb-0 text-[10px] font-bold text-gray-400 uppercase">Template</p>
              <div className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-gray-300" />
                <p className="!mb-0 text-xs font-bold text-black" data-test="metadata-template">
                  {templateText}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

SignMetadata.propTypes = {
  sign: PropTypes.object.isRequired,
  users: PropTypes.array.isRequired,
};
